"use client";

import { BlueGradientDivider } from "@/components/blueGradientDivider/blueGradientDivider.component";
import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";
import { Text } from "@/components/text/text.component";

import { ComparePlanTableRow } from "./comparePlanTableRow.component";
import type {
  SanityPlanCompareCategory,
  SanityPlanCompareRow,
} from "./useComparePlanTableSectionState";

export const ComparePlanTableCategory = ({
  category,
  gridTemplateColumns,
  columnCount,
}: ComparePlanTableCategoryProps) => {
  if (!category) return null;

  const rows = category.rows ?? [];
  const mobileGridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;

  const renderRows = () => {
    if (!rows.length) return null;

    return (
      <div data-testid="compare-plan-table-category-rows">
        {rows.map((row: SanityPlanCompareRow, rowIndex: number) => (
          <div key={row.id}>
            <ComparePlanTableRow
              row={row}
              rowIndex={rowIndex}
              rowCount={rows.length}
              gridTemplateColumns={gridTemplateColumns}
              mobileGridTemplateColumns={mobileGridTemplateColumns}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <details
      open
      className="group min-w-0 lg:col-[1/-1]"
      data-testid="compare-plan-table-category">
      <summary className="block cursor-pointer list-none [&::-webkit-details-marker]:hidden [&::marker]:hidden">
        <div className="flex items-center justify-between gap-4 px-4 py-4">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-3 lg:gap-y-1">
            <Text
              variant="h6"
              component="span"
              className="text-greyscale-headline shrink-0">
              {category.title}
            </Text>
            {category.description ? (
              <Text
                variant="body4"
                component="span"
                className="text-greyscale-body">
                {category.description}
              </Text>
            ) : null}
          </div>
          <ChevronDownIcon
            width={24}
            height={24}
            className="stroke-greyscale-body shrink-0 transition-transform duration-300 group-open:rotate-180"
            aria-hidden
          />
        </div>
        <BlueGradientDivider />
      </summary>

      {renderRows()}
    </details>
  );
};

type ComparePlanTableCategoryProps = {
  category: SanityPlanCompareCategory | null | undefined;
  gridTemplateColumns: string;
  columnCount: number;
};
